import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, Copy, ShieldCheck, ArrowsClockwise, Trash, Plugs } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

const WEBHOOK_EVENTS = ["checkout.session.completed", "checkout.session.async_payment_succeeded", "checkout.session.async_payment_failed", "checkout.session.expired"];

function AccountBadge({ s }) {
  const a = s.account;
  if (!a) return <Badge variant="outline" className="text-muted-foreground" data-testid="stripe-no-account">No Stripe account connected</Badge>;
  return (
    <>
      <Badge variant={s.enabled ? "default" : "secondary"} data-testid="stripe-account-name">
        {a.name || a.id} · {a.country} · {(a.currency || "").toUpperCase()}
      </Badge>
      <Badge variant="outline" className={a.mode === "live" ? "text-emerald-700 border-emerald-300" : "text-amber-700 border-amber-300"} data-testid="stripe-mode-badge">
        {a.mode === "live" ? "Live" : "Test mode"}
      </Badge>
      {!a.charges_enabled && (
        <span className="flex items-center gap-1 text-[11px] text-amber-700" data-testid="stripe-charges-warning">
          <WarningCircle size={13} weight="fill" /> Stripe hasn't enabled charges on this account yet — finish onboarding in your Stripe dashboard
        </span>
      )}
    </>
  );
}

export default function StripeKeysCard({ endpoint, variant = "tenant", title, intro }) {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [pub, setPub] = useState("");
  const [whsec, setWhsec] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [feePct, setFeePct] = useState("");
  const [probing, setProbing] = useState(false);

  const apply = (data) => {
    setS(data);
    setPub(data.publishable_key || "");
    setEnabled(!!data.enabled);
    setFeePct(data.connect_fee_pct != null ? String(data.connect_fee_pct) : "");
    setSecret("");
    setWhsec("");
    setDirty(false);
    setConfirmRemove(false);
  };

  useEffect(() => {
    API.get(endpoint).then(({ data }) => apply(data)).catch(() => toast.error("Failed to load Stripe settings"));
  }, [endpoint]);

  const edit = (fn) => { fn(); setDirty(true); };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const firstKey = !s.has_secret && !!secret.trim();
      const body = { publishable_key: pub, enabled: firstKey ? true : enabled, ...extra };
      if (variant === "platform") body.connect_fee_pct = feePct === "" ? 0 : Number(feePct);
      if (secret.trim()) body.secret_key = secret.trim();
      if (whsec.trim()) body.webhook_secret = whsec.trim();
      const { data } = await API.put(endpoint, body);
      apply(data);
      toast.success(extra.clear_secret ? "Stripe keys removed" : "Stripe settings saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Stripe settings");
      return false;
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await API.post(`${endpoint}/test`);
      toast.success(`Connected to ${data.account?.name || data.account?.id}`);
      const { data: fresh } = await API.get(endpoint);
      setS(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe connection failed");
      API.get(endpoint).then(({ data }) => setS(data)).catch(() => {});
    } finally { setTesting(false); }
  };

  const syncCatalog = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`${endpoint}/sync-catalog`);
      toast.success(`${data.prices} prices synced to Stripe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Catalogue sync failed");
    } finally { setSyncing(false); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { window.prompt(`Copy ${label}:`, text); }
  };

  const checkConnect = async () => {
    setProbing(true);
    try {
      const { data } = await API.post(`${endpoint}/connect-check`);
      if (data.available) toast.success("Stripe Connect is enabled — tenants can onboard with one click");
      else toast.error(data.error || "Stripe Connect isn't enabled on this account");
      setS((prev) => ({ ...prev, connect_probe: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect check failed");
    } finally { setProbing(false); }
  };

  if (!s) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  const isPlatform = variant === "platform";
  const usingEnv = isPlatform && s.source === "env";

  return (
    <div className="max-w-2xl space-y-5" data-testid="stripe-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">{title}</h3>
        <p className="text-xs text-muted-foreground">{intro}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid="stripe-status">
        {isPlatform && (
          <Badge variant="outline" data-testid="stripe-source-badge">
            {s.source === "settings" ? "Using keys saved here" : s.source === "env" ? `Using deployment keys ${s.env_hint}` : "No Stripe key anywhere"}
          </Badge>
        )}
        {!isPlatform && (
          <Badge variant={s.active_gateway ? "default" : "secondary"} data-testid="stripe-enabled-badge">
            {s.active_gateway === "connect" ? "Card payments ON via Stripe Connect" : s.active_gateway === "keys" ? "Card payments ON (own keys)" : "Card payments OFF"}
          </Badge>
        )}
        <AccountBadge s={s} />
        {s.verified_at && (
          <span className="flex items-center gap-1 text-[11px] text-emerald-700" data-testid="stripe-verified">
            <CheckCircle size={13} weight="fill" /> Verified {fmtDate(s.verified_at)}
          </span>
        )}
        {s.last_error && (
          <span className="flex items-center gap-1 text-[11px] text-red-600" data-testid="stripe-last-error">
            <WarningCircle size={13} weight="fill" /> {s.last_error}
          </span>
        )}
      </div>

      <div className="space-y-4 border border-border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{isPlatform ? "Use the keys saved here (instead of the deployment's)" : "Accept card payments on invoices"}</p>
            <p className="text-[10px] text-muted-foreground">
              {isPlatform ? "Switched on automatically when you save your first key. Off = subscriptions and licences keep using the STRIPE_* environment keys." : "Switched on automatically when you save your first key. Adds a \"Pay Online by Card\" button to invoice e-mails, reminders and the client portal."}
            </p>
          </div>
          <Switch checked={enabled} disabled={!s.has_secret && !secret.trim()} onCheckedChange={(v) => edit(() => setEnabled(v))} data-testid="stripe-enabled-toggle" />
        </div>

        <div>
          <Label className="text-xs font-semibold">Secret key</Label>
          <Input
            type="password"
            value={secret}
            onChange={(e) => edit(() => setSecret(e.target.value))}
            placeholder={s.has_secret ? `${s.secret_hint} (saved — paste a new key to replace)` : "sk_live_… or sk_test_…"}
            className="mt-1 font-mono"
            autoComplete="off"
            data-testid="stripe-secret-input"
          />
          {s.has_secret && (
            <p className="flex items-center gap-1 text-[10px] text-emerald-700 mt-1" data-testid="stripe-secret-saved">
              <ShieldCheck size={12} weight="fill" /> Stored encrypted — verified against Stripe when saved
            </p>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">Stripe Dashboard → Developers → API keys. A restricted key (rk_…) works if it can create Checkout Sessions and read the account.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold">Publishable key <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input value={pub} onChange={(e) => edit(() => setPub(e.target.value))} placeholder="pk_live_… or pk_test_…" className="mt-1 font-mono" data-testid="stripe-publishable-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Webhook signing secret <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input
              type="password"
              value={whsec}
              onChange={(e) => edit(() => setWhsec(e.target.value))}
              placeholder={s.has_webhook_secret ? "whsec_•••• (saved — paste to replace)" : "whsec_…"}
              className="mt-1 font-mono"
              autoComplete="off"
              data-testid="stripe-webhook-secret-input"
            />
          </div>
        </div>

        {isPlatform && (
          <div className="rounded-md border border-border p-3 space-y-2" data-testid="stripe-connect-platform-block">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Stripe Connect for tenants</p>
                <p className="text-[10px] text-muted-foreground">Lets tenants onboard with one click (Standard accounts under this platform). Needs Connect enabled on the account above.</p>
              </div>
              <Badge
                variant="outline"
                className={s.connect_probe?.available ? "text-emerald-700 border-emerald-300" : s.connect_probe?.available === false ? "text-red-600 border-red-300" : "text-muted-foreground"}
                data-testid="stripe-connect-probe-badge"
              >
                {s.connect_probe?.available ? "Connect enabled" : s.connect_probe?.available === false ? "Connect not enabled" : "Not checked"}
              </Badge>
            </div>
            {s.connect_probe?.available === false && s.connect_probe?.error && <p className="text-[11px] text-red-600" data-testid="stripe-connect-probe-error">{s.connect_probe.error}</p>}
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Label className="text-xs font-semibold">Platform fee per card payment (%)</Label>
                <Input type="number" step="0.1" min="0" max="20" value={feePct} onChange={(e) => edit(() => setFeePct(e.target.value))} className="mt-1" data-testid="stripe-connect-fee-input" />
              </div>
              <Button size="sm" variant="outline" onClick={checkConnect} disabled={probing || (!s.has_secret && !usingEnv)} className="gap-1" data-testid="stripe-connect-check-btn">
                <ArrowsClockwise size={13} className={probing ? "animate-spin" : ""} /> {probing ? "Checking…" : "Check Connect"}
              </Button>
              <a href="https://dashboard.stripe.com/connect/accounts/overview" target="_blank" rel="noreferrer" className="text-xs text-primary underline pb-2">Enable Connect in Stripe →</a>
            </div>
            <p className="text-[10px] text-muted-foreground">Applied as a Stripe application fee on Connect payments only (tenants using their own pasted keys can't be charged a fee).</p>
          </div>
        )}

        <div className="rounded-md bg-muted/40 p-3 space-y-1.5">
          <p className="text-[11px] font-medium">Webhook endpoint {isPlatform ? "" : "(optional — payments are confirmed when the client returns anyway)"}</p>
          <div className="flex items-center gap-2">
            <code className="text-[11px] break-all flex-1" data-testid="stripe-webhook-url">{s.webhook_url}</code>
            <Button size="sm" variant="ghost" className="h-7 px-2 shrink-0" onClick={() => copy(s.webhook_url, "Webhook URL")} data-testid="copy-stripe-webhook-url-btn">
              <Copy size={13} />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {isPlatform
              ? "Stripe → Developers → Webhooks → add this URL with checkout.session.completed, customer.subscription.*, invoice.paid and invoice.payment_failed, then paste the signing secret above."
              : `Stripe → Developers → Webhooks → add this URL listening for ${WEBHOOK_EVENTS.join(", ")}, then paste its signing secret above.`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => save()} disabled={saving || !dirty} data-testid="save-stripe-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
        <Button variant="outline" onClick={test} disabled={testing || saving || (!s.has_secret && !usingEnv)} className="gap-2" data-testid="test-stripe-btn">
          <Plugs size={15} /> {testing ? "Checking..." : "Test connection"}
        </Button>
        {isPlatform && (
          <Button variant="outline" onClick={syncCatalog} disabled={syncing || saving || (!s.has_secret && !usingEnv)} className="gap-2" data-testid="stripe-sync-catalog-btn">
            <ArrowsClockwise size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Re-sync price catalogue"}
          </Button>
        )}
        {s.has_secret && (
          confirmRemove ? (
            <span className="flex items-center gap-2 text-xs" data-testid="stripe-remove-confirm">
              Remove the saved keys{isPlatform ? "" : " and switch card payments off"}?
              <Button size="sm" variant="destructive" onClick={() => save({ clear_secret: true, clear_webhook_secret: true, enabled: false })} disabled={saving} data-testid="confirm-remove-stripe-keys-btn">Yes, remove</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(false)} data-testid="cancel-remove-stripe-keys-btn">Cancel</Button>
            </span>
          ) : (
            <Button variant="ghost" className="text-red-600 gap-1" onClick={() => setConfirmRemove(true)} data-testid="remove-stripe-keys-btn">
              <Trash size={14} /> Remove keys
            </Button>
          )
        )}
        {dirty && <span className="text-[11px] text-amber-700" data-testid="stripe-unsaved-hint">Unsaved changes</span>}
      </div>
    </div>
  );
}
